// User Analytics API Service - bridges to AnalyticsService backend quick endpoint

export interface UserMessageStats {
  date: string;
  messageCount: number;
  responseTime: number; // in minutes
  sentiment: 'positive' | 'neutral' | 'negative';
}

export interface GroupChatParticipation {
  groupId: string;
  groupName: string;
  joinDate: Date;
  messageCount: number;
  lastActive: Date;
  role: string;
  isActive: boolean;
}

export interface SentimentAnalysis {
  overall: 'positive' | 'neutral' | 'negative';
  positivePercentage: number;
  neutralPercentage: number;
  negativePercentage: number;
  recentTrend: 'improving' | 'stable' | 'declining';
  topKeywords: string[];
}

export interface UserActivitySummary {
  activeSince: Date;
  totalMessages: number;
  totalGroupChats: number;
  averageResponseTime: number; // in minutes
  mostActiveDay: string;
  mostActiveHour: number;
  streakDays: number;
}

export interface UserAnalytics {
  userId: string;
  messageStats: UserMessageStats[];
  groupChatParticipation: GroupChatParticipation[];
  sentimentAnalysis: SentimentAnalysis;
  activitySummary: UserActivitySummary;
  profileUrl?: string;
}

export interface UserConversationAnalytics {
  userId: string;
  conversationId: number;
  messagesInConversation: number;
  responseTimeMinutes: number;
  totalGroupChats: number;
  lastActive: string; // ISO datetime string
  sentimentScore?: number;
  positivePercentage: number;
}

class UserAnalyticsApiService {
  private static getHeaders(): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    try {
      const token = localStorage.getItem('series_analytics_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch {}
    return headers;
  }
  private static getBaseUrl(): string {
    // In production, use relative URLs to go through Vercel proxy
    if (import.meta.env.PROD) {
      return '';  // Use relative URLs in production (goes through Vercel proxy)
    }
    
    // In development, use configured URL or localhost
    const vite = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_ANALYTICS_API_BASE_URL)
      ? (import.meta as any).env.VITE_ANALYTICS_API_BASE_URL as string
      : undefined;
    const next = (typeof process !== 'undefined' && (process as any).env && (process as any).env.NEXT_PUBLIC_ANALYTICS_API_BASE_URL)
      ? (process as any).env.NEXT_PUBLIC_ANALYTICS_API_BASE_URL as string
      : undefined;
    return vite || next || 'http://localhost:8080';
  }

  private static async get<T>(path: string): Promise<T> {
    const res = await fetch(`${this.getBaseUrl()}${path}`, { headers: this.getHeaders(), credentials: 'include' });
    if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
    return res.json() as Promise<T>;
  }

  static async getUserAnalytics(userId: string): Promise<UserAnalytics> {
    // Fetch minimal analytics from backend and adapt to UI contract
    const quick = await this.get<{ user_id: string; total_messages: number; total_group_chats: number; average_response_time_minutes: number }>(
      `/api/v1/analytics/users/${encodeURIComponent(userId)}/quick`
    );

    const activitySummary: UserActivitySummary = {
      activeSince: new Date(),
      totalMessages: quick.total_messages,
      totalGroupChats: quick.total_group_chats,
      averageResponseTime: Math.round(quick.average_response_time_minutes * 10) / 10,
      mostActiveDay: '—',
      mostActiveHour: 0,
      streakDays: 0,
    };

    const emptyStats: UserMessageStats[] = [];
    const emptyGroups: GroupChatParticipation[] = [];
    const neutralSentiment: SentimentAnalysis = {
      overall: 'neutral',
      positivePercentage: 0,
      neutralPercentage: 100,
      negativePercentage: 0,
      recentTrend: 'stable',
      topKeywords: [],
    };

    return {
      userId: quick.user_id,
      messageStats: emptyStats,
      groupChatParticipation: emptyGroups,
      sentimentAnalysis: neutralSentiment,
      activitySummary,
      profileUrl: undefined,
    };
  }

  static async getUserConversationAnalytics(userId: string, conversationId: number): Promise<UserConversationAnalytics> {
    // Fetch conversation-specific analytics
    const response = await this.get<{
      user_id: string;
      conversation_id: number;
      messages_in_conversation: number;
      response_time_minutes: number;
      total_group_chats: number;
      last_active: string;
      sentiment_score?: number;
      positive_percentage: number;
    }>(`/api/v1/analytics/users/${encodeURIComponent(userId)}/conversations/${conversationId}/analytics`);

    return {
      userId: response.user_id,
      conversationId: response.conversation_id,
      messagesInConversation: response.messages_in_conversation,
      responseTimeMinutes: Math.round(response.response_time_minutes * 10) / 10,
      totalGroupChats: response.total_group_chats,
      lastActive: response.last_active,
      sentimentScore: response.sentiment_score,
      positivePercentage: Math.round(response.positive_percentage * 10) / 10,
    };
  }

  static async getQuickUserStats(userId: string): Promise<{
    totalMessages: number;
    groupChatsJoined: number;
    activeSince: Date;
    sentiment: string;
  }> {
    const analytics = await this.getUserAnalytics(userId);
    return {
      totalMessages: analytics.activitySummary.totalMessages,
      groupChatsJoined: analytics.activitySummary.totalGroupChats,
      activeSince: analytics.activitySummary.activeSince,
      sentiment: analytics.sentimentAnalysis.overall,
    };
  }
}

export default UserAnalyticsApiService;